import type { ApiService } from '@/types/dashboard-types'

export interface ApiTiming {
  /** Summed duration of every API call the insight made, in ms. */
  totalMs: number
  /** How many calls that was — a caption lookup on top of the data load counts as two. */
  calls: number
}

/**
 * Wraps the API so one insight load can be timed on its own.
 *
 * Scoped per load rather than accumulated globally because a dashboard loads its insights
 * concurrently — a shared counter would blend them, and the whole point here is to put a
 * Fresh and a Timescale copy of the same insight side by side and compare.
 *
 * A Proxy rather than a hand-written wrapper per verb so it covers simpleGet, the CSV paths
 * and anything added to ApiService later without this needing to know about them.
 *
 * Durations are summed, not wall-clocked: an insight that fires two calls in parallel
 * reports the total work done, which is what makes the two back ends comparable.
 */
export const withApiTiming = (api: ApiService): { api: ApiService; timing: ApiTiming } => {
  const timing: ApiTiming = { totalMs: 0, calls: 0 }

  const timed = new Proxy(api, {
    get(target, prop, receiver) {
      const value = Reflect.get(target, prop, receiver) as unknown
      if (typeof value !== 'function') {
        return value
      }
      return (...args: unknown[]) => {
        const startedAt = performance.now()
        const result = (value as (...a: unknown[]) => unknown).apply(target, args)
        if (!(result instanceof Promise)) {
          return result
        }
        timing.calls += 1
        // finally rather than then, so a failed call still counts toward the elapsed time.
        return result.finally(() => {
          timing.totalMs += performance.now() - startedAt
        })
      }
    },
  })

  return { api: timed, timing }
}
